/**
 * S-08 Onboarding — location consent (V1 auth-profiles-gdpr, PRD #24).
 * Explicit GDPR consent with the honest explanation (©onboard.location);
 * manual city is a FIRST-CLASS equal option, not a punishment. One of the
 * two is a server-side guard of ONBOARDING → ACTIVE — unskippable. Browser
 * permission denied auto-switches to the city picker (©onboard.location_denied).
 * Distance unit is a client display preference (localStorage).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { copy } from "@humble/shared";
import { Button, Card } from "../../design/index.js";
import { api, isApiError } from "../../lib/api.js";
import { OnboardingFrame } from "./onboarding-frame.js";
import "./onboarding.css";
import "../profile/profile.css"; // .chips / .chip-toggle (shared V1 primitives)

const UNIT_KEY = "humble.distance_unit";

function readUnit(): "km" | "mi" {
  try {
    return localStorage.getItem(UNIT_KEY) === "mi" ? "mi" : "km";
  } catch {
    return "km";
  }
}

export function LocationScreen() {
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [unit, setUnit] = useState<"km" | "mi">(readUnit());
  const [busyGps, setBusyGps] = useState(false);
  const [busyCity, setBusyCity] = useState(false);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function saveUnit(next: "km" | "mi") {
    setUnit(next);
    try {
      localStorage.setItem(UNIT_KEY, next);
    } catch {
      /* non-fatal */
    }
  }

  async function submitLocation(body: { consent?: boolean; lat?: number; lon?: number; manual_city?: string }) {
    await api.put("/onboarding/location", { body });
    navigate("/onboard/briefing");
  }

  function onUseGps() {
    if (busyGps) return;
    setBusyGps(true);
    setError(null);
    if (!("geolocation" in navigator)) {
      setDenied(true);
      setBusyGps(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        void (async () => {
          try {
            await submitLocation({
              consent: true,
              lat: pos.coords.latitude,
              lon: pos.coords.longitude,
            });
          } catch (err) {
            setError(isApiError(err) ? err.message : "Network error — try again.");
          } finally {
            setBusyGps(false);
          }
        })();
      },
      () => {
        setDenied(true);
        setBusyGps(false);
      },
      { timeout: 15_000, maximumAge: 60_000 },
    );
  }

  async function onUseCity() {
    if (busyCity) return;
    if (city.trim().length === 0) {
      setError("Type a city name first.");
      return;
    }
    setBusyCity(true);
    setError(null);
    try {
      await submitLocation({ manual_city: city.trim() });
    } catch (err) {
      setError(isApiError(err) ? err.message : "Network error — try again.");
    } finally {
      setBusyCity(false);
    }
  }

  return (
    <OnboardingFrame step={3} screen="S-08">
      <Card padding="lg">
        <p className="h-caps onboard__kicker">Step 3 of 4</p>
        <h1 className="type-display-3 onboard__title">Where do we point the deck?</h1>
        <p className="text-dim">{copy.onboard.location}</p>

        {denied && (
          <p role="status" className="text-dim" style={{ fontWeight: 600 }}>
            {copy.onboard.location_denied}
          </p>
        )}

        {!denied && (
          <Button
            block
            variant="primary"
            size="lg"
            loading={busyGps}
            onClick={onUseGps}
            style={{ marginBottom: 10 }}
          >
            Use my location
          </Button>
        )}

        <div className="field" style={{ marginTop: 6 }}>
          <label htmlFor="onboard-city">{denied ? "Pick your city" : "Or pick a city instead"}</label>
          <input
            id="onboard-city"
            type="text"
            maxLength={120}
            placeholder="e.g. Lyon"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>
        <Button block variant={denied ? "primary" : "ghost"} size="lg" loading={busyCity} onClick={() => void onUseCity()}>
          Use this city
        </Button>

        <p style={{ fontWeight: 600, margin: "14px 0 4px" }}>Distances in</p>
        <div className="chips">
          {(["km", "mi"] as const).map((u) => (
            <button
              key={u}
              type="button"
              className={`chip-toggle${unit === u ? " chip-toggle--on" : ""}`}
              onClick={() => saveUnit(u)}
            >
              {u === "km" ? "kilometers" : "miles"}
            </button>
          ))}
        </div>

        {error && (
          <p className="field__error" role="alert">
            {error}
          </p>
        )}
      </Card>
    </OnboardingFrame>
  );
}
